const CTA_POPUP_SELECTOR = ".cta-form-popup, .fancybox__content[id^='cta-form-']";

export const getDefaultCtaSuccessMessage = (thanksEl) => {
  if (!thanksEl) {
    return "";
  }

  const lang = (document.documentElement.lang || document.documentElement.getAttribute("xml:lang") || "de").toLowerCase();
  const isEn = lang.startsWith("en");

  return thanksEl.getAttribute(isEn ? "data-msg-en" : "data-msg-de")
    || thanksEl.getAttribute("data-msg-de")
    || "";
};

export const hasBackendCtaSuccessMessage = (thanksEl) => {
  return thanksEl?.getAttribute("data-has-backend-message") === "1";
};

export const isCtaPopupSuccessState = (formOrPopup) => {
  const root = formOrPopup?.closest?.(CTA_POPUP_SELECTOR)
    || formOrPopup?.closest?.(".fancybox__content")
    || formOrPopup;

  return root?.dataset?.ctaFormSuccess === "1";
};

export const extractConfirmationFromHtml = (html, formId) => {
  if (!html || !formId) {
    return null;
  }

  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const confirmationEl = doc.getElementById(formId);
    if (!confirmationEl) {
      return null;
    }

    const content = confirmationEl.innerHTML.trim();
    return content || null;
  } catch {
    return null;
  }
};

const normalizeSuccessHtml = (html) => {
  const trimmed = (html || "").trim();
  if (!trimmed) {
    return "";
  }

  const singleParagraph = trimmed.match(/^<p[^>]*>([\s\S]*)<\/p>$/i);
  if (singleParagraph) {
    return singleParagraph[1].trim();
  }

  return trimmed;
};

const resolvePopupRoot = (form) => {
  const fancyContent = form.closest(".fancybox__content");
  if (fancyContent) {
    return fancyContent;
  }

  const popup = form.closest(CTA_POPUP_SELECTOR);
  if (popup) {
    return popup;
  }

  const contactForm = form.closest(".contact-form");
  return contactForm?.parentElement || null;
};

const resolveThanksElement = (popupRoot) => {
  const siblingThanks = popupRoot?.querySelector(":scope > .cta-form-success[data-ajax-thanks]");
  if (siblingThanks) {
    return siblingThanks;
  }

  return popupRoot?.querySelector("[data-ajax-thanks]") || null;
};

const isCtaPopupForm = (form) => {
  if (!form) {
    return false;
  }

  if (form.dataset.ctaPopupForm === "1") {
    return true;
  }

  if (form.closest(".cta-form-popup, .fancybox__content[id^='cta-form-']")) {
    return true;
  }

  const contactParent = form.closest(".contact-form")?.parentElement;
  return Boolean(contactParent?.querySelector(":scope > .cta-form-success[data-ajax-thanks]"));
};

export const resolveCtaPopupContext = (form) => {
  if (!isCtaPopupForm(form)) {
    return null;
  }

  const popupRoot = resolvePopupRoot(form);
  const thanksEl = popupRoot ? resolveThanksElement(popupRoot) : null;

  if (!popupRoot || !thanksEl) {
    return null;
  }

  return { popup: popupRoot, thanksEl };
};

export const markCtaPopupForms = () => {
  document.querySelectorAll(
    ".cta-form-popup form.default-form, .fancybox__content[id^='cta-form-'] form.default-form"
  ).forEach((form) => {
    form.dataset.ctaPopupForm = "1";
  });
};

const resolveSuccessMessage = (thanksEl, options = {}) => {
  const messageEl = thanksEl.querySelector(".cta-form-success__message");

  if (hasBackendCtaSuccessMessage(thanksEl) && messageEl?.innerHTML.trim()) {
    return normalizeSuccessHtml(messageEl.innerHTML);
  }

  const backendMessage = extractConfirmationFromHtml(options.responseHtml || "", options.formId || "");
  if (backendMessage) {
    return normalizeSuccessHtml(backendMessage);
  }

  if (!hasBackendCtaSuccessMessage(thanksEl)) {
    return getDefaultCtaSuccessMessage(thanksEl);
  }

  return normalizeSuccessHtml(messageEl?.innerHTML || "");
};

const renderSuccessMessage = (thanksEl, options = {}) => {
  const messageEl = thanksEl.querySelector(".cta-form-success__message") || thanksEl;
  const message = resolveSuccessMessage(thanksEl, options);

  if (!message) {
    return;
  }

  if (message.includes("<")) {
    messageEl.innerHTML = message;
    return;
  }

  messageEl.innerHTML = `<p class="mb-0">${message}</p>`;
};

const hidePopupSection = (node) => {
  if (!node) {
    return;
  }

  node.classList.add("d-none");
  node.style.setProperty("display", "none", "important");
  node.setAttribute("aria-hidden", "true");
};

export const showCtaFormSuccess = (popup, options = {}) => {
  const popupRoot = popup?.closest?.(".fancybox__content")
    || popup?.closest?.(CTA_POPUP_SELECTOR)
    || popup;

  if (!popupRoot) {
    return;
  }

  popupRoot.classList.add("cta-form-popup");
  popupRoot.dataset.ctaFormSuccess = "1";

  const thanksEl = options.thanksEl || resolveThanksElement(popupRoot);
  if (!thanksEl) {
    return;
  }

  renderSuccessMessage(thanksEl, options);

  hidePopupSection(popupRoot.querySelector(".contact-form"));
  hidePopupSection(popupRoot.querySelector(".cta-form-loader"));

  popupRoot.querySelectorAll(":scope > .cta-form-success").forEach((node) => {
    if (node !== thanksEl) {
      node.remove();
    }
  });

  thanksEl.classList.remove("d-none");
  thanksEl.removeAttribute("aria-hidden");
  thanksEl.style.removeProperty("display");
};

export const finishCtaPopupSuccess = (popup, form, options = {}) => {
  const submitButtons = form.querySelectorAll(".btn--form-submit, .form-navigation .btn");
  submitButtons.forEach((button) => {
    button.classList.remove("btn--form-submiting");
    button.disabled = false;
  });

  document.dispatchEvent(new CustomEvent("form:submit-end"));
  showCtaFormSuccess(popup, options);
};

// Backwards-compatible exports used by form.js
export const applyCtaSuccessMessage = (messageEl, thanksEl, options = {}) => {
  if (!messageEl || !thanksEl) {
    return "";
  }

  const message = resolveSuccessMessage(thanksEl, options);
  if (!message) {
    return "";
  }

  if (message.includes("<")) {
    messageEl.innerHTML = message;
  } else {
    messageEl.textContent = message;
  }

  return message;
};
